// src/components/SideNavigation.tsx
import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X as XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {Tooltip} from '@dopomogai/ui';

const NAV_ICON_BUTTON_ACTIVE_SHADOW_VALUE = '3px 3px 0px 0px rgb(17,24,39)';
const sectionColorMapping: Record<string, { bg: string; text: string; border: string; }> = {
  'default-section': { bg: 'bg-emerald-100/70', text: 'text-emerald-700', border: 'border-emerald-500' },
};

export interface SideNavItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  activeIcon?: React.ReactNode;
  activeColorClasses?: {
    bg: string;
    text: string;
    border: string;
  };
  inactiveColorClasses?: {
    bg: string;
    text: string;
    border: string;
  };
}

interface SideNavigationProps {
  sections: SideNavItem[];
  activeSectionId: string | null;
  onNavigate: (sectionId: string) => void;
  className?: string;
  isMobileLayout: boolean;
  isMobileDrawerOpen?: boolean;
  onMobileDrawerClose?: () => void;
  variant?: 'horizontal' | 'vertical'; // <-- NEW PROP
}

const ICON_SIZE_DESKTOP = 20;
const ICON_SIZE_MOBILE_DRAWER = 20;

export const SideNavigation: React.FC<SideNavigationProps> = ({
  sections,
  activeSectionId,
  onNavigate,
  className = '',
  isMobileLayout,
  isMobileDrawerOpen,
  onMobileDrawerClose,
  variant = 'horizontal', // <-- Default to horizontal
}) => {
  const { t } = useTranslation('agent-common');
  const mobileDrawerRef = useRef<HTMLDivElement>(null);

  // --- Mobile Drawer Logic (No changes needed here) ---
  useEffect(() => {
    // ... (logic for closing drawer on outside click and escape key remains the same)
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileDrawerRef.current && !mobileDrawerRef.current.contains(event.target as Node)) {
        onMobileDrawerClose?.();
      }
    };
    if (isMobileDrawerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileDrawerOpen, onMobileDrawerClose]);
  
  // Mobile drawer is always a vertical list, so it's a good single source of truth for that style.
  const mobileAndVerticalNavList = (
    <ul className="flex-grow overflow-y-auto py-2">
      {sections.map((section) => {
        const isActive = section.id === activeSectionId;
        const activeClasses = section.activeColorClasses || { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-500' };
        const inactiveClasses = section.inactiveColorClasses || { bg: 'bg-transparent', text: 'text-slate-700', border: 'border-transparent'};
        
        return (
          <li key={section.id} className="px-2">
            <button
              onClick={() => {
                onNavigate(section.id);
                if (isMobileLayout) onMobileDrawerClose?.();
              }}
              className={clsx(`w-full text-left px-3 py-2 my-0.5 flex items-center group rounded-md transition-all duration-150 ease-out`, `focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-emerald-400`,
                isActive ? `${activeClasses.bg} ${activeClasses.text} font-semibold border-l-4 ${activeClasses.border}` : `${inactiveClasses.bg} ${inactiveClasses.text} hover:bg-slate-100 hover:text-slate-900 border-l-4 ${inactiveClasses.border}`)}
              aria-current={isActive ? 'page' : undefined}
            >
              {section.icon && React.cloneElement(section.icon as React.ReactElement, { size: ICON_SIZE_MOBILE_DRAWER, className: clsx('mr-3 shrink-0 transition-colors duration-200', isActive ? activeClasses.text : 'text-slate-500 group-hover:text-slate-700')})}
              <span className="truncate">{section.name}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );


  // --- Mobile Drawer ---
  if (isMobileLayout) {
    return (
      <AnimatePresence>
        {isMobileDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={onMobileDrawerClose} aria-hidden="true"
            />
            <motion.nav
              ref={mobileDrawerRef as React.RefObject<HTMLElement>}
              initial={{ x: '-100%' }} animate={{ x: '0%' }} exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
              className={clsx("fixed top-0 left-0 h-full w-72 sm:w-80 bg-white shadow-xl z-50 flex flex-col", className)}
              aria-label={t('sideNavigation.mobileNavLabel', 'Page sections')}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold text-slate-800">{t('sideNavigation.mobileMenuTitle', 'Page Sections')}</h2>
                <button onClick={onMobileDrawerClose} className="p-1 text-slate-500 hover:text-slate-700 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500" aria-label={t('common.close', 'Close navigation menu')}>
                  <XIcon size={22} />
                </button>
              </div>
              {mobileAndVerticalNavList}
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    );
  }


  // --- Desktop Navigation (Conditional Rendering based on variant) ---

  if (variant === 'vertical') {
    return (
        <nav className={clsx("w-full", className)} aria-label={t('sideNavigation.desktopNavLabel', 'Page sections')}>
            {mobileAndVerticalNavList}
        </nav>
    );
  }

  // --- Default: Horizontal Icon Bar ---
  return (
    <motion.nav
      className={clsx(
        `inline-block rounded-xl border-2 border-black 
         bg-white/70 backdrop-blur-sm transition-colors duration-200 ease-out
         hover:bg-white/100`,
        "relative z-10",
        className
      )}
      aria-label={t('sideNavigation.desktopNavLabel', 'Page sections')}
    >
      <div className="overflow-x-auto overflow-y-visible whitespace-nowrap scrollbar-hide px-3 pt-3 pb-3">
        <ul className="flex items-center space-x-1.5 sm:space-x-2">
          {sections.map((section) => {
            const isActive = section.id === activeSectionId;
            const activeItemColors = section.activeColorClasses || (sectionColorMapping[section.id] || sectionColorMapping['default-section']);
            const inactiveItemColors = section.inactiveColorClasses || { bg: 'bg-slate-100/60', text: 'text-slate-500', border: 'border-slate-400' };
            const currentColors = isActive ? activeItemColors : inactiveItemColors;
            const hoverBg = !isActive ? activeItemColors.bg.replace(/\/50|\/60|\/70|\/80/, '') : '';
            const hoverText = !isActive ? activeItemColors.text : '';
            const hoverBorder = !isActive ? activeItemColors.border : '';
            const buttonSizeClass = 'w-10 h-10 sm:w-11 sm:h-11';
            const currentIcon = isActive && section.activeIcon ? section.activeIcon : section.icon;

            return (
              <li key={section.id} className="relative flex-shrink-0">
                <Tooltip text={section.name} position="bottom" disabled={isActive}>
                  <motion.button
                    onClick={(e) => {
                      onNavigate(section.id);
                      (e.currentTarget as HTMLButtonElement).blur();
                    }}
                    className={clsx(
                      `${buttonSizeClass} rounded-lg border-2 flex items-center justify-center group relative
                      transition-colors duration-150 ease-out focus:outline-none 
                      focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-emerald-500`,
                      currentColors.bg,
                      currentColors.text,
                      currentColors.border,
                      !isActive && `hover:${hoverBg} hover:${hoverText} hover:${hoverBorder}`
                    )}
                    aria-label={t('sideNavigation.navigateTo', { sectionName: section.name })}
                    aria-current={isActive ? 'page' : undefined}
                    initial={{ boxShadow: '0px 0px 0px 0px rgba(0,0,0,0)' }}
                    animate={{ boxShadow: isActive ? NAV_ICON_BUTTON_ACTIVE_SHADOW_VALUE : '0px 0px 0px 0px rgba(0,0,0,0)' }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    whileHover={!isActive ? { x: -3, y: -3, boxShadow: `3px 3px 0px 0px rgb(17,24,39)` } : { x: -1, y: -1, scale: 1.02, boxShadow: NAV_ICON_BUTTON_ACTIVE_SHADOW_VALUE }}
                    whileTap={!isActive ? { x: 1, y: 1, scale: 0.98, boxShadow: '0px 0px 0px 0px rgba(0,0,0,0)' } : { x: 0, y: 0, scale: 0.98, boxShadow: NAV_ICON_BUTTON_ACTIVE_SHADOW_VALUE }}
                  >
                    {React.cloneElement(currentIcon as React.ReactElement, {
                        size: ICON_SIZE_DESKTOP,
                        className: 'transition-transform duration-100 ease-out group-hover:scale-110'
                    })}
                  </motion.button>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.nav>
  );
};

